/**
 * The documentation that ships with a release — the installation and
 * management guides, the change-log document.
 *
 * These are not a fourth artifact kind: they are ordinary Documentation-section
 * Documents PINNED to the release (`default_release_id` — only the pin since
 * 2026-10-31: a data sheet merely mapped to R11.0 for its audience is library
 * material and is not listed here)
 * and shown here as a second window onto the very records the Documentation
 * section groups under the version. Adding one uploads through the
 * Documentation tab's own path with the release pinned
 * (`ReleaseDocumentUploadDialog`); who may READ it is decided upstream by the
 * one resolver and shown here only as the audience line.
 *
 * DOCUMENTS BELONG TO A VERSION, not to the release above it: 10.2.0.27 ships
 * its own installation and management guides the same way it ships its own
 * disk images. (portal-api will still serve a document pinned to the release
 * itself to every version under it — `Release#documents_scope` — but nothing
 * here writes at that level, so there is one place to look and one place to
 * upload.)
 *
 * TAKING ONE OFF (2026-10-31). Each row's ⋮ menu offers **Remove from this
 * version** — portal-api unpins it from this one release; it stays in its
 * Documentation folder with its audience unchanged — and **Delete document**, which deletes it
 * everywhere. Both go through `<ConfirmDialog>`, and both are offered only on
 * a document the viewer may edit (`useDocumentCapabilities().canModify`:
 * any document for `documentation:edit`, their own for `documentation:create`).
 */
import { Alert, Box, Button, Chip, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, FilePlus2, MoreVertical } from "lucide-react";
import { useState, type JSX } from "react";
import { Link as RouterLink } from "react-router-dom";
import type { DocAccess, DocSummary } from "@xop/dashboard-shared";

import { PageLoader } from "@/components/ui/page-loader";
import { useDocumentCapabilities } from "../../../auth/permissions.js";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { ApiError } from "../../../lib/api.js";
import { deleteDoc, detachDocFromRelease, listReleaseDocuments } from "../../../pages/documentation/api.js";
import { ReleaseDocumentUploadDialog } from "./ReleaseDocumentUploadDialog.js";
import type { ReleaseDTO } from "./releases-api.js";

/** What a document's audience reads as on the card. Mirrors the resolver's
 *  vocabulary; the decision itself is portal-api's. */
export function audienceLabel(access: DocAccess | undefined): string {
  switch (access?.scope) {
    case "feature": {
      const names = access.features.map((f) => f.name ?? f.slug);
      return names.length ? `Owners of ${names.join(", ")}` : "Feature owners";
    }
    case "product": {
      const names = access.products.map((p) => p.name ?? p.code);
      return names.length ? `Owners of ${names.join(", ")}` : "Product owners";
    }
    case "release":
      return "Customers on this release";
    case "private":
      return access.private_to_org ? `Only ${access.private_to_org.name}` : "One customer";
    default:
      return "Everyone";
  }
}

const DOC_TYPE_LABEL: Record<string, string> = {
  guide: "Guide",
  article: "Article",
  video: "Video",
  faq: "FAQ",
  slide: "Slides",
  file: "File",
};

interface Props {
  release: ReleaseDTO;
  /** May add documents — the Documentation authoring tiers
   *  (`documentation:create` / `documentation:edit`), NOT `admin:users`:
   *  writing a guide is a docs job, curating disk images an admin one. */
  canAuthorDocs: boolean;
}

type PendingAction = { kind: "remove" | "delete"; doc: DocSummary };

export function ReleaseDocuments({ release, canAuthorDocs }: Props): JSX.Element {
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const caps = useDocumentCapabilities();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["admin", "release-documents", release.id],
    queryFn: () => listReleaseDocuments(release.id),
  });
  const docs: DocSummary[] = query.data ?? [];

  const action = useMutation({
    mutationFn: ({ kind, doc }: PendingAction) =>
      kind === "remove" ? detachDocFromRelease(doc.id, release.id) : deleteDoc(doc.id),
    onSuccess: async () => {
      setActionError(null);
      await qc.invalidateQueries({ queryKey: ["admin", "release-documents"] });
      await qc.invalidateQueries({ queryKey: ["docs"] });
    },
    onError: (err) => {
      setActionError(err instanceof ApiError && err.message ? err.message : "That did not work. Try again.");
    },
  });

  return (
    <Box sx={{ display: "grid", gap: 1 }} data-testid={`release-${release.version}-documents`}>
      {query.isLoading ? (
        <PageLoader />
      ) : query.isError ? (
        <Alert
          severity="error"
          sx={{ fontSize: 13 }}
          action={
            <Button size="small" onClick={() => void query.refetch()}>
              Retry
            </Button>
          }
        >
          {query.error instanceof ApiError && query.error.message
            ? query.error.message
            : "Could not load this version's documents."}
        </Alert>
      ) : docs.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          No documents ship with {release.version} yet
          {canAuthorDocs ? " — add the installation and management guides below." : "."}
        </Typography>
      ) : (
        docs.map((doc) => (
          <DocumentRow
            key={doc.id}
            doc={doc}
            version={release.version}
            {...(canAuthorDocs && caps.canModify(doc) ? { onAction: (kind: PendingAction["kind"]) => setPending({ kind, doc }) } : {})}
          />
        ))
      )}

      {actionError && (
        <Alert severity="error" sx={{ fontSize: 13 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <ConfirmDialog
        isOpen={pending?.kind === "remove"}
        onClose={() => setPending(null)}
        title={`Remove from ${release.version}?`}
        description={
          pending ? (
            <>
              <strong>{pending.doc.title}</strong> will no longer ship with {release.version}. The document is not
              deleted — it stays in its Documentation folder.
            </>
          ) : undefined
        }
        variant="warning"
        confirmLabel="Remove from this version"
        onConfirm={async () => {
          if (pending) await action.mutateAsync(pending).catch(() => undefined);
          setPending(null);
        }}
      />
      <ConfirmDialog
        isOpen={pending?.kind === "delete"}
        onClose={() => setPending(null)}
        title="Delete this document?"
        description={
          pending ? (
            <>
              <strong>{pending.doc.title}</strong> will be deleted everywhere — from this version, from every other
              release it ships with, and from its Documentation folder.
            </>
          ) : undefined
        }
        variant="destructive"
        confirmLabel="Delete document"
        onConfirm={async () => {
          if (pending) await action.mutateAsync(pending).catch(() => undefined);
          setPending(null);
        }}
      />

      {canAuthorDocs && (
        <Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilePlus2 size={14} />}
            onClick={() => setAdding(true)}
            aria-label={`Add document to ${release.version}`}
          >
            Add document
          </Button>
          {adding && (
            <ReleaseDocumentUploadDialog
              releaseId={release.id}
              version={release.version}
              productName={release.product_name}
              open
              onClose={() => setAdding(false)}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

function DocumentRow({
  doc,
  version,
  onAction,
}: {
  doc: DocSummary;
  version: string;
  /** Present only when the viewer may edit this document. */
  onAction?: (kind: PendingAction["kind"]) => void;
}): JSX.Element {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const pick = (kind: PendingAction["kind"]): void => {
    setAnchor(null);
    onAction?.(kind);
  };
  return (
    <Box
      data-testid={`release-${version}-doc-${doc.id}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        opacity: doc.status === "published" ? 1 : 0.7,
      }}
    >
      <Box sx={{ display: "inline-flex", color: "primary.main", flexShrink: 0 }}>
        <BookOpen size={18} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            component={RouterLink}
            to={`/documentation/library/${encodeURIComponent(doc.slug)}`}
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "text.primary",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {doc.title}
          </Typography>
          <Chip
            label={DOC_TYPE_LABEL[doc.doc_type] ?? doc.doc_type}
            size="small"
            variant="outlined"
            sx={{ height: 18 }}
          />
          {doc.status !== "published" && (
            <Chip
              label={doc.status === "draft" ? "Draft" : doc.status}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 18 }}
            />
          )}
        </Box>
        <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
          {audienceLabel(doc.access)}
          {doc.category ? ` · ${doc.category.name}` : " · not filed in a folder"}
        </Typography>
      </Box>
      {onAction && (
        <>
          <IconButton
            size="small"
            aria-label={`Actions for ${doc.title}`}
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            <MoreVertical size={16} />
          </IconButton>
          <Menu anchorEl={anchor} open={anchor != null} onClose={() => setAnchor(null)}>
            <MenuItem onClick={() => pick("remove")}>Remove from this version</MenuItem>
            <MenuItem onClick={() => pick("delete")} sx={{ color: "error.main" }}>
              Delete document
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
}
